import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { IMAGES } from "../siteConfig";

const LABEL = "Illustrative example — not a real client commission";

const DemoMap = () => (
  <svg
    viewBox="0 0 480 360"
    role="img"
    aria-label="Demonstration map with field boundaries and contour lines"
    className="h-full w-full"
    data-testid="demo-map-svg"
  >
    <rect width="480" height="360" fill="#E9E3D6" />
    <g fill="none" stroke="#A7B5A0" strokeWidth="1.2" opacity="0.8">
      <path d="M-20 80 C 80 40, 200 60, 300 40 S 460 50, 520 30" />
      <path d="M-20 120 C 90 80, 210 100, 310 80 S 470 90, 520 70" />
      <path d="M-20 160 C 100 120, 220 145, 320 125 S 480 135, 520 115" />
      <path d="M-20 205 C 110 165, 230 190, 330 170 S 490 180, 520 160" />
      <path d="M-20 250 C 120 210, 240 240, 340 220 S 500 230, 520 210" />
      <path d="M-20 300 C 130 260, 250 290, 350 270 S 510 280, 520 260" />
      <path d="M-20 345 C 140 310, 260 335, 360 320 S 520 325, 520 310" />
    </g>
    <g fill="none" stroke="#244B3B" strokeWidth="2">
      <path d="M60 40 L 210 30 L 235 130 L 90 150 Z" />
      <path d="M235 30 L 420 55 L 395 160 L 240 130 Z" />
      <path d="M90 155 L 235 135 L 260 250 L 100 265 Z" />
      <path d="M245 140 L 395 165 L 370 280 L 262 252 Z" />
      <path d="M100 270 L 260 255 L 285 340 L 115 350 Z" />
    </g>
    <path d="M60 40 L 420 55" stroke="#B88A45" strokeWidth="2" strokeDasharray="6 5" fill="none" />
    <g fontFamily="Montserrat, sans-serif" fontSize="11" fill="#29312D">
      <text x="120" y="95">Field A · 12.4 ac</text>
      <text x="300" y="100">Field B · 9.7 ac</text>
      <text x="140" y="205">Woodland</text>
      <text x="290" y="215">Field C · 14.1 ac</text>
      <text x="160" y="310">Track &amp; access</text>
    </g>
    <g fill="#B88A45">
      <circle cx="60" cy="40" r="4" />
      <circle cx="420" cy="55" r="4" />
    </g>
  </svg>
);

export default function ExampleWork() {
  return (
    <>
      <section data-testid="example-hero" className="bg-forest pb-20 pt-40 text-limestone md:pb-28 md:pt-52">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal>
            <span data-testid="example-label" className="inline-block bg-ochre px-3 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-forest-deep">
              {LABEL}
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              One estate. One clear picture.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-limestone/80 md:text-lg">
              This is a fictional demonstration estate — "Hazleton Fields" — created to show how deliverables
              could be organised for a rural property. The place, boundaries and measurements are invented.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-20 md:px-8 md:py-28">
        <div className="grid gap-8 lg:grid-cols-2">
          <Reveal className="relative">
            <img
              src={IMAGES.fields}
              alt="Illustrative aerial view over fictional demonstration estate fields"
              loading="lazy"
              className="h-full min-h-[320px] w-full object-cover"
            />
            <div className="absolute inset-0">
              {[
                { top: "22%", left: "30%", label: "Overview capture" },
                { top: "55%", left: "62%", label: "Woodland edge" },
                { top: "74%", left: "24%", label: "Access track" },
              ].map((pin) => (
                <div key={pin.label} className="group absolute" style={{ top: pin.top, left: pin.left }}>
                  <MapPin className="h-6 w-6 -translate-x-1/2 -translate-y-full text-ochre drop-shadow" fill="#F4F0E7" strokeWidth={1.5} />
                  <span className="absolute left-3 top-0 whitespace-nowrap bg-forest px-2 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.15em] text-limestone opacity-0 transition-opacity group-hover:opacity-100">
                    {pin.label}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.12} className="border border-forest/15">
            <DemoMap />
          </Reveal>
        </div>

        <div className="mt-16 grid gap-10 md:grid-cols-3">
          {[
            { title: "What this shows", body: "A dated aerial image set, an annotated overview map and a concise visual summary — the kind of pack that makes a property easier to discuss and plan around." },
            { title: "What it could support", body: "Briefing a contractor before work begins, keeping a seasonal record, or giving advisers a shared visual reference for a conversation." },
            { title: "Honest limitations", body: "Demonstration measurements are invented. Real outputs and their accuracy depend on the project, capture method and processing — and are never a legal boundary determination." },
          ].map((c, i) => (
            <Reveal key={c.title} delay={0.08 * i} className="border-t border-forest/20 pt-6">
              <h2 className="font-serif text-xl text-forest">{c.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-charcoal/80">{c.body}</p>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16 flex flex-col items-start justify-between gap-8 bg-forest p-10 text-limestone md:flex-row md:items-center md:p-14">
            <h2 className="max-w-md font-serif text-2xl leading-snug md:text-3xl">
              What would a pack like this show on your land?
            </h2>
            <Link
              to="/contact"
              data-testid="example-page-cta"
              className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-ochre px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-forest-deep transition-colors hover:bg-limestone"
            >
              Discuss your mapping project
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
